export type ExplosionClassName = 'pushka' | 'geran' | 'roked' | 'shots' | 'none';

export interface SensorPos {
  lat: number;
  lon: number;
  deviceId: string;
}

/** Per-sensor localisation trace: range-equivalent timing residual + outlier flag. */
export interface PerSensorTrace {
  deviceId: string;
  lat: number;
  lon: number;
  residualM: number;
  isOutlier: boolean;
}

export interface Explosion {
  id: string;
  latitude: number;
  longitude: number;
  gdop: number;
  confidence: number;
  converged: boolean;
  method: string;
  sensorCount: number;
  className: ExplosionClassName;
  rmsResidual: number;
  uncertaintyRadiusM: number;
  quality?: LocalizationQuality;
  createdAt: number;
  sensorPositions?: SensorPos[];
  perSensor?: PerSensorTrace[];
}

export type RansacBaseSolver = 'chan-ho' | 'gauss-newton';

export interface SimParams {
  lat: number;
  lon: number;
  nSensors: number;
  onsetNoiseMs: number;
  gpsNoiseM: number;
  className: ExplosionClassName;
  solver: 'gauss-newton' | 'chan-ho' | 'bancroft' | 'ransac' | 'grid-search';
  ransacBaseSolver: RansacBaseSolver;
  residualThresholdM: number;
  radiusKm: number;
}

export interface SimSensor {
  index: number;
  lat: number;
  lon: number;
  distanceM: number;
  travelTimeMs: number;
  appliedNoiseMs: number;
  onsetTimeMs: number;
  residualM?: number;
  isOutlier?: boolean;
}

export interface SimTdoa {
  lat: number;
  lon: number;
  gdop: number;
  converged: boolean;
  nIterations: number;
  method: string;
  gdopPassed: boolean;
}

export interface SimResult {
  created: number;
  groundTruth: { lat: number; lon: number };
  sensors: SimSensor[];
  tdoa: SimTdoa | null;
  errorMeters: number | null;
}

export interface ClusterUpdatePayload {
  id: string;
  latitude: number;
  longitude: number;
  gdop: number;
  confidence: number;
  converged: boolean;
  method: string;
  sensorCount: number;
  className: ExplosionClassName;
  rmsResidual: number;
  createdAt: number;
  quality?: LocalizationQuality;
  uncertaintyRadiusM?: number;
  sensorPositions?: SensorPos[];
  perSensor?: PerSensorTrace[];
}

export type LocalizationQuality = 'provisional' | 'coarse' | 'reliable';

/** Broadcast for a localised moving source (drone / rocket / aircraft fly-by). */
export interface TrajectoryUpdatePayload {
  id: string;
  className: ExplosionClassName;
  /** Stable MovingTrack id this snapshot belongs to (replace-by-track on the client). */
  trackId?: string | null;
  originLat: number;
  originLon: number;
  headingDeg: number;
  speedMps: number;
  polyline: Array<{ lat: number; lon: number }>;
  curvePolyline?: Array<{ lat: number; lon: number }>;
  cpaPoints?: TrajectoryCpaPoint[];
  cost: number;
  converged: boolean;
  quality: LocalizationQuality;
  posSigmaM: number;
  headingSigmaDeg: number;
  sensorCount: number;
  createdAt: number;
  sensorPositions?: SensorPos[];
  perSensor?: PerSensorTrace[];
}

/** A localised moving source as kept in the client store / IndexedDB cache. */
export interface Trajectory {
  id: string;
  className: ExplosionClassName;
  /** Stable MovingTrack id this snapshot belongs to (dedupe / replace-by-track). */
  trackId?: string | null;
  originLat: number;
  originLon: number;
  headingDeg: number;
  speedMps: number;
  polyline: Array<{ lat: number; lon: number }>;
  curvePolyline?: Array<{ lat: number; lon: number }>;
  cpaPoints?: TrajectoryCpaPoint[];
  cost: number;
  converged: boolean;
  quality: LocalizationQuality;
  posSigmaM: number;
  headingSigmaDeg: number;
  sensorCount: number;
  createdAt: number;
  sensorPositions?: SensorPos[];
  perSensor?: PerSensorTrace[];
}

// === Continuous moving-object tracking (MovingTrack) ===

export type TrackStatus = 'tentative' | 'confirmed' | 'coasting' | 'terminated';

/** One point of a track's path (history) or forward projection (predicted). */
export interface MovingTrackPoint {
  lat: number;
  lon: number;
  /** Epoch milliseconds. */
  t: number;
  /** 1σ position uncertainty at this point, metres. */
  sigmaM: number;
}

/**
 * Live state of a continuously tracked flying object, pushed over `track-update`.
 * Mirrors the backend `TrackPayload`. Unlike a one-shot `Trajectory`, a track carries a
 * stable `trackId`, a growing `history` polyline and a `predicted` dead-reckoning cone, so
 * the client animates one moving marker over time instead of dropping disconnected arrows.
 */
export interface TrackUpdatePayload {
  trackId: string;
  className: ExplosionClassName;
  status: TrackStatus;
  lat: number;
  lon: number;
  headingDeg: number;
  speedMps: number;
  posSigmaM: number;
  hits: number;
  /** SPRT log-LR confidence score (directional, P_D-weighted). */
  score: number;
  /** IMM manoeuvre-model probability (0 = cruising straight, →1 = weaving). */
  maneuverProb: number;
  history: MovingTrackPoint[];
  predicted: MovingTrackPoint[];
  lastUpdateMs: number;
}

/** A continuously tracked moving object as kept in the client store. */
export type MovingTrack = TrackUpdatePayload;

export interface FetchExplosionsParams {
  limit?: number;
  className?: ExplosionClassName;
  minGdop?: number;
  maxGdop?: number;
  fromTimestamp?: number;
  toTimestamp?: number;
}

export const EXPLOSION_CLASS_COLORS: Record<ExplosionClassName, string> = {
  pushka: '#e74c3c',
  geran: '#e67e22',
  roked: '#9b59b6',
  shots: '#f39c12',
  none: '#95a5a6',
};

export const EXPLOSION_CLASS_LABELS: Record<ExplosionClassName, string> = {
  pushka: 'Explosion',
  geran: 'MLRS',
  roked: 'Rocket',
  shots: 'Shots',
  none: 'None',
};

/**
 * Per-class glyphs shared across the radar UI (mic chip, own-events feed). Single source of
 * truth so the icon next to the microphone matches the one in the detection history.
 */
export const EXPLOSION_CLASS_ICONS: Record<ExplosionClassName, string> = {
  none: '🔇',
  geran: '🛸', // Shahed-type drone
  pushka: '💥', // explosion / artillery
  roked: '🚀', // rocket / jet fly-by
  shots: '🔫', // gunfire
};

export const DEFAULT_SIM_PARAMS: SimParams = {
  lat: 50.4501,
  lon: 30.5234,
  nSensors: 5,
  onsetNoiseMs: 100,
  gpsNoiseM: 20,
  className: 'pushka',
  solver: 'gauss-newton',
  ransacBaseSolver: 'chan-ho',
  residualThresholdM: 50,
  radiusKm: 10,
};

export const SOLVERS = [
  {
    id: 'gauss-newton' as const,
    label: 'Gauss-Newton',
    params: 'iterative NLS',
    description: 'Левенберг-Марквардт. Устойчив к шуму. 12-50 итераций.',
  },
  {
    id: 'chan-ho' as const,
    label: 'Chan-Ho',
    params: 'closed-form WLS',
    description: 'Двухэтапное прямое решение. Быстрее GN, менее устойчив при высоком шуме.',
  },
  {
    id: 'bancroft' as const,
    label: 'Bancroft',
    params: 'algebraic, N≥4',
    description: 'Алгебраическое решение через пространство Лоренца. O(N), нет итераций.',
  },
  {
    id: 'ransac' as const,
    label: 'RANSAC',
    params: '60 iter, thr=50m',
    description: 'Случайные подмножества + инлайер-голосование. Устойчив к выбросам.',
  },
  {
    id: 'grid-search' as const,
    label: 'Grid Search',
    params: 'brute-force 60×60',
    description: 'Двухпроходный перебор сетки. Гарантирует глобальный минимум.',
  },
];

// === Algorithm Monitoring / Processing Report ===
// Mirror of apps/backend/src/modules/events/processing-report.types.ts — the staged trace
// of how a detection was processed (recall → associate → route → localize).

export type LocalizationMode = 'static' | 'moving' | 'ignore';

export type ProcessingStage =
  | 'recall'
  | 'associate'
  | 'route'
  | 'localize-static'
  | 'localize-moving';

/** A trajectory point with the flight time it corresponds to (seconds, relative to launch). */
export interface GroundTruthPoint {
  lat: number;
  lon: number;
  tRelSec: number;
}

/** Simulator ground truth carried through for the monitoring overlay (dev-only). */
export interface GroundTruth {
  /** True source position at each fired sensor's detection instant, time-ordered. */
  points: GroundTruthPoint[];
  /** Full sampled true flight path. */
  path: GroundTruthPoint[];
}

/** Estimated source position at a sensor's closest approach, with the time it occurred. */
export interface TrajectoryCpaPoint {
  lat: number;
  lon: number;
  deviceId: string;
  /** Time of closest approach, seconds (absolute). */
  tCpa: number;
  /** Minimum slant range at CPA, metres. */
  dCpa: number;
}

export interface ReportSensor {
  deviceId: string;
  lat: number;
  lon: number;
  /** Range-equivalent timing residual, metres (localisation stages). */
  residualM?: number;
  isOutlier?: boolean;
  /** Cluster id joined at the associate stage; null = dropped as noise / other cluster. */
  clusterId?: number | null;
  /** Peak-energy arrival time of this sensor, seconds (absolute). Moving localisation. */
  onsetTimeS?: number;
}

export interface StageNode {
  stage: ProcessingStage;
  note?: string;
  receivedCount?: number;
  clusters?: number;
  noiseCount?: number;
  usedCount?: number;
  filteredCount?: number;
  perSensor?: ReportSensor[];
  gdop?: number;
  gdopPassed?: boolean;
  cost?: number;
  method?: string;
  quality?: LocalizationQuality;
  uncertaintyM?: number;
  /** Cluster start time — earliest sensor onset, ms (moving localisation). */
  t0Ms?: number;
}

export interface StaticResult {
  kind: 'static';
  latitude: number;
  longitude: number;
  gdop: number;
  confidence: number;
  quality: LocalizationQuality;
  uncertaintyRadiusM: number;
  method: string;
  sensorCount: number;
}

export interface MovingResult {
  kind: 'moving';
  originLat: number;
  originLon: number;
  headingDeg: number;
  speedMps: number;
  polyline: Array<{ lat: number; lon: number }>;
  /** Manoeuvre-aware bending reconstruction; falls back to `polyline`. */
  curvePolyline?: Array<{ lat: number; lon: number }>;
  /** Estimated source position at each sensor's closest approach, time-ordered. */
  cpaPoints?: TrajectoryCpaPoint[];
  cost: number;
  converged: boolean;
  quality: LocalizationQuality;
  posSigmaM: number;
  headingSigmaDeg: number;
  sensorCount: number;
  /** Cluster start time — earliest sensor onset, ms. */
  t0Ms?: number;
  /** Simulator ground truth for the overlay (dev-only; absent in production). */
  groundTruth?: GroundTruth;
}

export type ProcessingResult = StaticResult | MovingResult;

export interface EventProcessingReport {
  id: string;
  ts: number;
  triggerDeviceId?: string;
  className: string;
  mode: LocalizationMode;
  stages: StageNode[];
  result: ProcessingResult | null;
  rejection?: string;
  resultId?: string;
  /** True when reconstructed from a persisted document (recall/associate counts absent). */
  synthesized?: boolean;
}

// === WebSocket Event Types ===

export interface EventDetectedPayload {
  eventId: string;
  className: ExplosionClassName;
  confidence: number;
  timestamp: number;
}

export interface NotificationPayload {
  id: string;
  type: 'cluster_complete' | 'localization_failed' | 'system';
  message: string;
  timestamp: number;
  data?: Record<string, unknown>;
}

export interface WsErrorPayload {
  code: string;
  message: string;
}

export interface ClientSubscribePayload {
  bounds?: { north: number; south: number; east: number; west: number };
  className?: ExplosionClassName;
}

/**
 * Country-wide air-raid alert snapshot pushed by the backend (aggregated
 * alerts.in.ua + UkraineAlarm). `legacy` — проекция в старый ubilling-контракт
 * (карта по имени области); `threats`/`targets` — новые данные (авиа-угрозы,
 * точечные цели). Форма зеркалит `AlertsSnapshot` в alertStore.
 */
export interface AlertsUpdatePayload {
  legacy: {
    states: Record<string, { alertnow: boolean; changed: string }>;
    cachedat: string | null;
    fetchedAt: number | null;
  };
  threats: {
    hasBallistics: boolean;
    hasStrategicAviation: boolean;
    hasTacticalAviation: boolean;
    hasBoats: boolean;
    lastUpdate: number | null;
  } | null;
  targets: Array<{ lat: number; lon: number; type: string; lastSeen: number }>;
  meta: { fetchedAt: number | null; lastUpdatedAt: number | null; sources: string[] };
}

export interface MonitorQuadrant {
  /** Grid row index (= Math.floor(lat / QUADRANT_LAT_DELTA)). */
  row: number;
  /** Grid column index (= Math.floor(lon / QUADRANT_LON_DELTA)). */
  col: number;
  /** Number of monitors inside this quadrant. */
  count: number;
}

export interface QuadrantAlert {
  row: number;
  col: number;
  /** Number of recent explosions in this quadrant (last 30 s). */
  count: number;
}

export interface ServerToClientEvents {
  'cluster-update': (payload: ClusterUpdatePayload) => void;
  'trajectory-update': (payload: TrajectoryUpdatePayload) => void;
  'track-update': (payload: TrackUpdatePayload) => void;
  'event-detected': (payload: EventDetectedPayload) => void;
  'notification': (payload: NotificationPayload) => void;
  /** How many *other* people are monitoring within the user's radius right now, plus quadrant heatmap. */
  'monitors-count': (payload: { count: number; quadrants: MonitorQuadrant[] }) => void;
  /** Explosion detected in a quadrant — highlight it red for 30 s. */
  'quadrant-alert': (payload: QuadrantAlert) => void;
  /** Air-raid alert states for all oblasts; sent on connect-time fetch gaps via REST instead. */
  'alerts-update': (payload: AlertsUpdatePayload) => void;
  'connect': () => void;
  'disconnect': () => void;
  'error': (payload: WsErrorPayload) => void;
}

export interface ClientToServerEvents {
  'subscribe': (payload: ClientSubscribePayload) => void;
  'unsubscribe': () => void;
  /** Declare the map viewport [minLat, minLon, maxLat, maxLon]; null clears the filter. */
  'subscribe-region': (bbox: [number, number, number, number] | null) => void;
  /** Go online as a monitor at (lat, lon) — only sent when a GPS/manual fix exists.
   *  deviceId связывает подключение с detection-событиями (Telegram-уведомления). */
  'start-monitor': (payload: { lat: number; lon: number; deviceId?: string }) => void;
  /** Go offline as a monitor (mic off / leaving). */
  'stop-monitor': () => void;
}
